import { useMutation } from "@apollo/client";
import { Button, useModal } from "@fluidattacks/design";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { AddMembersModal } from "../../modals/add-modal";
import { ADD_STAKEHOLDER_MUTATION } from "../queries";
import { handleGrantError } from "../utils";
import type { IStakeholderFormValues } from "features/add-user-modal/types";
import type { OrganizationRole } from "gql/graphql";
import type { IStakeholderDataSet } from "pages/organization/members/types";
import { msgSuccess } from "utils/notifications";

interface IAddActionProps {
  organizationId: string;
  selectedMembers: IStakeholderDataSet[];
  allMembers: IStakeholderDataSet[];
  resetOnAction: VoidFunction;
}

const AddAction = (props: IAddActionProps): JSX.Element => {
  const { organizationId, resetOnAction, selectedMembers, allMembers } = props;
  const { t } = useTranslation();

  const modalRef = useModal("addStakeholderModal");
  const onClick = useCallback((): void => {
    modalRef.open();
  }, [modalRef]);

  const [grantStakeholderAccess] = useMutation(ADD_STAKEHOLDER_MUTATION, {
    onCompleted: (mtResult): void => {
      if (mtResult.grantStakeholderOrganizationAccess.success) {
        resetOnAction();
        const { email } =
          mtResult.grantStakeholderOrganizationAccess.grantedStakeholder ?? {};
        msgSuccess(
          `${t("searchFindings.tabUsers.success")} ${email}`,
          t("organization.tabs.users.successTitle"),
        );
      }
    },
    onError: (grantError): void => {
      handleGrantError(grantError);
    },
  });

  const handleSubmit = useCallback(
    async (values: IStakeholderFormValues): Promise<void> => {
      await grantStakeholderAccess({
        variables: {
          email: values.email,
          organizationId,
          role: values.role as OrganizationRole,
        },
      });
      resetOnAction();
      modalRef.close();
    },
    [grantStakeholderAccess, organizationId, resetOnAction, modalRef],
  );

  return (
    <React.Fragment>
      <Button id={"add-member"} onClick={onClick}>
        {t("organization.members.addUserButton.text")}
      </Button>
      {modalRef.isOpen && (
        <AddMembersModal
          action={"add"}
          allMembers={allMembers}
          close={modalRef.close}
          isOpen={modalRef.isOpen}
          onSubmit={handleSubmit}
          organizationId={organizationId}
          selectedMembers={selectedMembers}
        />
      )}
    </React.Fragment>
  );
};

export { AddAction };
